import { Autocomplete, TextField } from "@mui/material";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

export type OptionType<T> = {
  value: string | number;
  label?: string;
  data: T;
};

export function AppAutoCompleteWithSearchAPI<T>({
  value,
  onChange,
  placeholder,
  getData,
  disabled = false,
  getOptionLabel,
  filterOptions,
  onInputChange,
  inputValue,
  refetchKey, // NEW: change this to force a fresh page-0 fetch (e.g. warehouse id)
}: {
  initialPageNumber: number;
  value: OptionType<T> | null;
  onChange: (event: any, value: OptionType<T> | null) => void;
  placeholder?: string;
  label?: string;
  getData: (data: {
    currentPageNumber: number;
    currentSearchQueryTerm: string;
  }) => Promise<{ options: OptionType<T>[]; hasMore: boolean }>;
  disabled?: boolean;
  getOptionLabel?: (option: OptionType<T>) => string;
  filterOptions?: (options: OptionType<T>[]) => OptionType<T>[];
  onInputChange?: (event: React.SyntheticEvent, value: string) => void;
  inputValue?: string;
  refetchKey?: string | number;
}) {
  const [options, setOptions] = useState<OptionType<T>[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const [currentSearchQueryTerm, setCurrentSearchQueryTerm] = useState<string>(
    value?.label?.toString() || ""
  );
  const currentSearchQueryTermRef = useRef(currentSearchQueryTerm);
  const [hasMore, setHasMore] = useState(true);

  const isFetching = useRef(false);
  const hasMoreRef = useRef(true);
  const requestIdRef = useRef(0);
  const previousRefetchKeyRef = useRef(refetchKey);
  const getDataRef = useRef(getData);
  getDataRef.current = getData;
  
  const fetchPage = useCallback(
    ({
      currentPageNumber: pageNumber,
      currentSearchQueryTerm: term,
    }: {
      currentPageNumber: number;
      currentSearchQueryTerm: string;
    }) => {
      // Only "load more" pages are blocked by hasMore / in-flight; a new search (page 0) always runs
      if (pageNumber > 0 && (!hasMoreRef.current || isFetching.current)) return;

      const requestId = ++requestIdRef.current;
      isFetching.current = true;

      getDataRef
        .current({ currentPageNumber: pageNumber, currentSearchQueryTerm: term })
        .then(({ options: newOptions, hasMore: more }) => {
          if (requestId !== requestIdRef.current) return; // a newer request superseded this one
          setOptions((prev) =>
            pageNumber === 0 ? newOptions : [...prev, ...newOptions]
          );
          hasMoreRef.current = more;
          setHasMore(more);
          setCurrentPageNumber(pageNumber + 1);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) isFetching.current = false;
        });
    },
    []
  );

  const debouncedFetch = useRef(debounce(fetchPage, 500)).current;

  // Cancel pending debounced call on unmount
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  useEffect(() => {
    debouncedFetch({
      currentPageNumber: 0,
      currentSearchQueryTerm: value?.label || "",
    });
  }, [debouncedFetch, value]);

  useEffect(() => {
    if (previousRefetchKeyRef.current === refetchKey) return;

    previousRefetchKeyRef.current = refetchKey;
    debouncedFetch.cancel();
    requestIdRef.current += 1;
    hasMoreRef.current = true;
    setOptions([]);
    setCurrentPageNumber(0);
    setHasMore(true);
    fetchPage({
      currentPageNumber: 0,
      currentSearchQueryTerm: currentSearchQueryTermRef.current,
    });
  }, [debouncedFetch, fetchPage, refetchKey]);

  function getDataOnEndScroll(e: React.SyntheticEvent) {
    const bottom =
      e.currentTarget.scrollHeight - e.currentTarget.scrollTop <=
      e.currentTarget.clientHeight + 30;
    if (bottom && hasMore) {
      debouncedFetch({ currentPageNumber, currentSearchQueryTerm });
    }
  }

  return (
    <Autocomplete
      options={options}
      loading={isFetching.current}
      getOptionLabel={
        getOptionLabel
          ? (option) => {
              if (typeof option === "string") return option;
              return getOptionLabel(option as OptionType<T>);
            }
          : (option) => {
              if (typeof option === "string") return option;
              return `${option.label || option.value}`;
            }
      }
      filterOptions={
        filterOptions ? filterOptions : (options: OptionType<T>[]) => options
      }
      value={value}
      inputValue={inputValue}
      isOptionEqualToValue={(option: OptionType<T>, value: OptionType<T>) =>
        option.value.toString() === value.value.toString()
      }
      onChange={(event, value) => {
        if (typeof value === "string") return;
        onChange(event, value as OptionType<T> | null);
      }}
      onInputChange={(event, newInputValue) => {
        debouncedFetch({
          currentPageNumber: 0,
          currentSearchQueryTerm: newInputValue,
        });
        currentSearchQueryTermRef.current = newInputValue;
        setCurrentSearchQueryTerm(newInputValue);
        if (onInputChange) onInputChange(event, newInputValue);
      }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            style: { fontSize: "0.875rem" },
          }}
        />
      )}
      ListboxProps={{
        onScroll: getDataOnEndScroll,
        style: { maxHeight: 200, overflow: "auto" },
      }}
      fullWidth
      freeSolo
    />
  );
}